<template>
  <div>
    <div
      v-if="task.id === 'create'"
      v-click-outside="() => createTask(true)"
      :class="[$style.card, $style.create_mode]"
    >
      <textarea
        ref="nameInput"
        v-model="name"
        :class="$style.name_input"
        :placeholder="locale('board_task_enter_name')"
        rows="1"
        @click.stop
        @keydown.enter.prevent="() => createTask()"
      />
      <div
        ref="nameDisplay"
        :class="[$style.name, $style.create_mode]"
      >
        {{ name }}
      </div>
    </div>
    <div
      v-else
      ref="mainWrapper"
      v-tippy="{
        trigger: 'manual',
        theme: 'board-card',
        placement: 'right-start',
        content: tippyContent,
        boundary: tippyBoundary,
        maxWidth: 210,
        popperOptions: {
          modifiers: [
            {
              name: 'flip',
              options: {
                boundary: tippyBoundary,
                flipVariations: true,
              },
            },
          ],
        }
      }"
      :class="[
        $style.card,
        highlightOverdue && isTaskOvedue ? $style.overdue : '',
        settingsOpened ? $style.settings_opened : '',
        'card-wrapper'
      ]"
      @click="cardClickHandler"
    >
      <div :class="$style.header">
        <div
          :class="$style.path"
          @mouseover.stop="toggleCardTip(true)"
          @mouseleave.stop="toggleCardTip(false)"
        >
          <span
            v-for="breadcrumb in breadcrumbs.shortened"
            :key="breadcrumb"
          >
            {{ breadcrumb }}
          </span>
        </div>
        <div :class="[$style.name_and_color]">
          <div
            :class="[
              $style.color,
              'inner_clickable',
              paletteOpened ? $style.palette_opened : '',
              nameEditing ? $style.disable_hover : ''
            ]"
          >
            <vgp-color-palette
              ref="colorPalette"
              :class="$style.color_picker"
              :disabled="isFieldDisabled('color', task)"
              select-type="single"
              select-field="square"
              :selected-values="[task.color]"
              :colors="colors.data"
              :select-width="'72px'"
              :select-height="'32'"
              drop-position="fixed"
              small
              @showPalette="colorPaletteHandler"
              @selectColors="(e) => handleChangeData('color', e[0].id)"
            />
          </div>
          <textarea
            v-show="nameEditing"
            ref="nameInput"
            v-model="name"
            :class="[$style.name_input, $style.edit_mode]"
            :placeholder="locale('board_task_enter_name')"
            @click.stop
            @blur="updateName"
            @keydown.enter.prevent="updateName"
          />
          <div
            ref="nameDisplay"
            :class="[
              $style.name,
              nameEditing ? $style.edit_mode : ''
            ]"
            @mouseover.stop="toggleCardTip(true)"
            @mouseleave.stop="toggleCardTip(false)"
          >
            {{ name }}
          </div>
        </div>
      </div>

      <div :class="$style.primary_info">
        <div
          :class="$style.left"
          @click="hideResourcePopup"
        >
          <div
            v-if="groupingFieldInfo.key !== 'status'"
            :class="[
              $style.status,
              'inner_clickable',
              isStatusEditing ? $style.active : '',
              isFieldDisabled('status', task) ? $style.disabled : ''
            ]"
            @click="(e) => handleClickStatusAndPriority(e, 'status')"
          >
            <svg-sprite
              :name="taskStatus.icon"
              type="bold"
              :size="16"
              :style="{ color: taskStatus.color }"
            />
            <div :class="$style.status__label">
              {{ taskStatus.value }}
            </div>
          </div>
          <div
            v-if="groupingFieldInfo.key !== 'priority'"
            :class="[
              $style.priority,
              'inner_clickable',
              isPriorityEditing ? $style.active : '',
              isFieldDisabled('priority', task) ? $style.disabled : ''
            ]"
            @click="(e) => handleClickStatusAndPriority(e, 'priority')"
          >
            <svg-sprite
              :name="taskPriority.icon"
              type="bold"
              :size="16"
              :style="{ color: taskPriority.color }"
            />
            <div :class="$style.priority__label">
              {{ taskPriority.value }}
            </div>
          </div>
        </div>
        <div :class="$style.right">
          <assigned-select
            :class="[
              $style.resources,
              'inner_clickable',
              isFieldDisabled('resources', task) ? $style.disabled : ''
            ]"
            :resources="formattedResources"
            :max-visible="3"
            :is-active="isAssignResourcesPopupShown"
            :tippyBoundary="tippyBoundary"
            @onClick="showAssignResourcesPopup"
          />
        </div>
      </div>

      <div :class="$style.footer">
        <div
          ref="dates"
          :class="$style.dates"
          @click="hideResourcePopup"
        >
          <div
            :class="[
              $style.date_editor,
              'inner_clickable',
            ]"
          >
            <DateEditor
              ref="startDate"
              :value="task.start_date"
              :columnName="'start_date'"
              :workDays="workDays"
              :calendarConfig="calendarConfig"
              :disabled="isFieldDisabled('start_date', task)"
              @showCalendar="(flag) => showCalendar('startDate', flag)"
              @onChange="(val) => handleChangeDate('start_date', val)"
            />
          </div>
          <div :class="$style.separator">
            -
          </div>
          <div
            :class="[
              $style.date_editor,
              highlightOverdue && isTaskOvedue && $style.overdue,
              'inner_clickable',
            ]"
          >
            <DateEditor
              ref="endDate"
              :value="task.end_date"
              :columnName="'end_date'"
              :minDate="task.start_date"
              :workDays="workDays"
              :calendarConfig="calendarConfig"
              :disabled="isFieldDisabled('end_date', task)"
              @showCalendar="(flag) => showCalendar('endDate', flag)"
              @onChange="(val) => handleChangeDate('end_date', val)"
            />
          </div>
        </div>
        <div
          :class="$style.status_bar"
          @click="hideResourcePopup"
        >
          <div
            v-if="task.hasComments"
            :class="[
              $style.comments,
              'inner_clickable',
              newCommentsCount ? $style.new : ''
            ]"
            @click="() => taskSettingsHandler('comments')"
          >
            <svg-sprite
              name="comments"
              :size="20"
            />
          </div>
          <div
            v-if="task.hasAttachments"
            :class="[$style.attachments, 'inner_clickable']"
            @click="() => taskSettingsHandler('attachments')"
          >
            <svg-sprite
              name="attach"
              :size="20"
            />
          </div>
        </div>
      </div>

      <div
        v-if="!(nameEditing || taskSettingsInfo)"
        :class="[$style.context_menu, 'inner_clickable']"
        @click="hideResourcePopup"
      >
        <iconButton
          :icon="{ name: 'more-1', type: 'regular', size: 20 }"
          :colors="{ color: '#808080', bg: '#E5E5E5', hoverBg: '#CCCCCC', activeBg: '#B2B2B2', activeColor: '#808080' }"
          @onClick="(e) => toggleContextMenu({ ...task, columnId }, 'task', 'main', e.target)"
        />
      </div>

      <div
        v-if="taskSettingsInfo || task.id === 'draft'"
        :class="[
          $style.mouse_events_blocker,
          task.id === 'draft' ? $style.mouse_loader : ''
        ]"
        @click="taskSettingsHandler"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import app from '$$app';
import AssignedSelect from './AssignedSelect.vue';
import iconButton from '$$vueCmp/globalButton/withIcon/iconButton.vue';
import vgpDatePicker from '$$vueCmp/datePicker/vgpDatePicker.vue';
import DateEditor from './DateEditor.vue';

import dateHelper from '$$helpers/dateFormats';
import { getWorkloadUnitLocale, getTaskBreadcrumbs } from '../../../helpers/boardHelpers.js';
import customHelper from '../../../helpers/custom';
import rights from '../../../components/rights';
import constants from '$$helpers/constants';

const isDate = d => d instanceof Date && !isNaN(d);

export default {
  name: 'TaskCard',
  components: {
    AssignedSelect,
    iconButton,
    vgpDatePicker,
    DateEditor,
  },
  inject: [
    'toggleContextMenu',
    'showTaskSettings',
    'isFieldDisabled',
    'checkPricingAccessOverdue',
    'getTasksMap',
    'getProjectName',
    'hideProjectSettingsPopup',
    'getCalendarConfig',
    'getClosestDate',
    'hideResourcePopup',
    'closeContextMenus',
  ],
  props: {
    columnId: {
      type: [Number, String],
      required: true,
      default: 0,
    },
    task: {
      type: Object,
      default: {}
    },
    projectResources: {
      type: Object,
      default: {}
    }
  },
  emits: [
    'taskChange',
    'createTask',
  ],
  data() {
    return {
      locale: __,
      isAssignResourcesPopupShown: false,
      positionContextTaskMain: null,
      positionContextColumnValues: null,
      name: '',
      nameEditing: false,
      tippyTimeout: null,
      color: 1,
      paletteOpened: false,
      createTriggered: false,
      openedDateEditorRef: null
    };
  },
  created() {
    this.name = this.task.text || '';
    this.color = this.task.color || 1;
  },
  computed: {
    ganttId() {
      return +this.$route.params.projectId;
    },
    statusField() {
      return this.getField(false, 'status');
    },
    taskStatus() {
      if (this.task.id === 'create') return {};

      return this.statusField.options.find(opt => opt.id === this.task.status);
    },
    taskPriority() {
      if (this.task.id === 'create') return {};

      return this.priorityField.options.find(opt => opt.id === this.task.priority);
    },
    priorityField() {
      return this.getField(false, 'priority');
    },
    formattedResources() {
      const formattedResources = [];
      const hasCostRight = rights.project.hasCostRight(+this.task.gantt_id);

      if (this.task.id !== 'create') {
        this.task.resources.forEach(({ resource_id, value }) => {
          const projectRes = this.projectResources[resource_id];

          if (!projectRes) return;

          const workloadType = projectRes.resourceProjects.find(p => p.projectId === this.task.gantt_id)?.type;

          formattedResources.push({
            resource_id,
            userId: projectRes.userId,
            name: projectRes.name,
            avatar: projectRes.photo || projectRes.resourcePhoto || `${GT.cdn}/imgs/default/default-ava.svg`,
            workload: value,
            workloadType,
            workloadUnit: workloadType ? __(getWorkloadUnitLocale({ workloadType })) : '',
            hideValuesInTemplates: workloadType === constants.RESOURCE_COST_TYPE && !hasCostRight,
          });
        });
      }

      return formattedResources;
    },
    breadcrumbs() {
      if (this.task.id === 'create') return {};

      return getTaskBreadcrumbs({
        parentId: this.task.parent,
        tasksMap: this.getTasksMap(),
      });
    },
    isTaskOvedue() {
      if (this.task.id === 'create') return false;

      return new Date(this.task.end_date) < new Date() && this.task.progress < 1 && this.task.type === 'task';
    },
    newCommentsCount() {
      return this.$store.state.comments.taskCommentsMetaData[this.task.id]?.unreadComments.size;
    },
    tippyContent() {
      if (this.task.id === 'create') return '';

      return `
        <div class="task-name">${customHelper.formatTaskName(this.task.text)}</div>
        <div style="display: flex; align-items: center; width: 100%; padding: 0 4px; min-height: 24px;">
          <div style="background: #E5E5E5; min-height: 1px; flex-grow: 1;"></div>
        </div>
        <div>${this.locale('board_path')}:<br>${this.breadcrumbs.all.join(' / ') || this.getProjectName()}</div>
      `;
    },
    tippyBoundary() {
      return document.querySelector('.board_container');
    },
    openedContextMenus() {
      if (this.task.id === 'create') return [];

      return this.contextMenus.filter(context =>
        context.type === 'task'
        && context.item.id === this.task.id
        && context.item.columnId === this.columnId
      );
    },
    isStatusEditing() {
      return this.openedContextMenus.some(context => context.menu === 'status');
    },
    isPriorityEditing() {
      return this.openedContextMenus.some(context => context.menu === 'priority');
    },
    highlightOverdue() {
      return this.checkPricingAccessOverdue('overdue_tasks') && this.$store.getters['headerView/isOverdueTasks'];
    },
    settingsOpened() {
      if (!this.taskSettingsInfo) return;

      const taskIdMatch = this.taskSettingsInfo.taskId === this.task.id;
      const columnMatch = this.taskSettingsInfo.columnId
        ? this.taskSettingsInfo.columnId === this.columnId
        : true;

      return taskIdMatch && columnMatch;
    },
    workDays() {
      const daysConfig = this.getCalendarConfig().dates;

      return Object.keys(daysConfig).filter((key) => daysConfig[key]);
    },
    calendarConfig() {
      return this.getCalendarConfig();
    },
    ...mapGetters({
      groupingFieldInfo: 'board/groupingFieldInfo',
      getField: 'board/getField',
      contextMenus: 'board/contextMenus',
      nameEditTaskKey: 'board/nameEditTaskKey',
      customFields: 'board/customFields',
      taskCardEditing: 'board/taskCardEditing',
      taskSettingsInfo: 'board/taskSettingsInfo',
      datesWidth: 'board/datesWidth',
      colors: 'teamSetting/colors',
      getResourceById: 'resourcesModel/getResourceById',
      getDataForBreadcrumbsByParentId: 'tasksModel/getDataForBreadcrumbsByParentId',
      getValuesMapForTask: 'columns/getValuesMapForTask',
      getAttachmentsByProjectAndTaskId: 'attachments/getAttachmentsByProjectAndTaskId',
    }),
  },
  watch: {
    'task.text': function (newVal) {
      this.name = newVal;
    },
    'task.color': function (newVal) {
      this.color = newVal;
    },
    name() {
      if (this.nameEditing) {
        setTimeout(() => {
          this.adjustInputHeight();
        });
      }
    },
    nameEditTaskKey(newVal) {
      if (newVal === `${this.columnId}:${this.task.id}`) {
        this.$refs.nameInput.style.height = `${this.$refs.nameDisplay.scrollHeight > 28 ? 48 : 28}px`;
        this.$refs.nameDisplay.style.width = `${this.$refs.nameDisplay.scrollWidth}px`;
        this.nameEditing = true;
        this.setTaskCardEditing({
          columnId: this.columnId,
          taskId: this.task.id
        });
        this.$nextTick(() => {
          setTimeout(() => {
            this.$refs.nameInput.focus();
          }, 0);
        });
      }
    },
    isAssignResourcesPopupShown(newVal) {
      const taskCardEditing = newVal ? {
        columnId: this.columnId,
        taskId: this.task.id
      } : null;
      this.setTaskCardEditing(taskCardEditing);
    },
    openedContextMenus(newVal, oldVal) {
      if (newVal.length === oldVal.length) return;

      const taskCardEditing = newVal.length ? {
        columnId: this.columnId,
        taskId: this.task.id
      } : null;
      this.setTaskCardEditing(taskCardEditing);
    },
    openedDateEditorRef(newVal, oldVal) {
      if (newVal && oldVal) return;

      const taskCardEditing = newVal ? {
        columnId: this.columnId,
        taskId: this.task.id
      } : null;
      this.setTaskCardEditing(taskCardEditing);
    },
    taskCardEditing(newVal) {
      this.toggleContainersScrollListener(!!newVal);
    },
    'task.id': {
      handler(newValue) {
        if (newValue === 'create') {
          this.nameEditing = true;
          setTimeout(() => {
            this.$refs.nameDisplay.style.width = `${this.$refs.nameInput.scrollWidth}px`;
            this.$refs.nameInput.style.height = `${this.$refs.nameDisplay.scrollHeight > 28 ? 48 : 28}px`;
          });
        }
      },
      immediate: true,
    },
  },
  methods: {
    updateName(e) {
      this.$refs.nameDisplay.style.width = '100%';
      this.nameEditing = false;
      this.$store.dispatch('board/clearNameEditTaskKey');
      this.handleChangeData('text', this.name.trim());
    },
    adjustInputHeight() {
      const newHeight = this.$refs.nameDisplay.scrollHeight > 28 ? 48 : 28;

      this.$refs.nameInput.style.height = `${newHeight}px`;
    },
    handleChangeData(key, value) {
      this.setTaskCardEditing(null);

      if (value === this.task[key]) return;

      switch (key) {
      case 'color':
        this.color = value;
        break;
      case 'text':
        this.name = value;
        break;
      }

      this.$emit('taskChange', { taskData: { ...this.task }, key, value });
    },
    onAssignResourcesPopupHide() {
      this.isAssignResourcesPopupShown = false;
    },
    showAssignResourcesPopup(event) {
      if (this.isFieldDisabled('resources', this.task) || this.isAssignResourcesPopupShown) return;

      const pos = event.currentTarget.getBoundingClientRectWrapper();

      // pos.x += 8;

      app.trigger('showResourcesListForTask', pos, this.task.id, this.task.gantt_id, 'boardView', this.onAssignResourcesPopupHide);

      this.isAssignResourcesPopupShown = true;
    },
    handleChangeDate(dateProp, newValue) {
      if (isDate(newValue) && isDate(this.task[dateProp])) {
        const oldValue = this.task[dateProp];
        const newValueWithLimitCheck = dateHelper.checkIfDateInLimit(newValue, true);

        const newDateClone = new Date(newValue.getTime());

        newDateClone.setHours(0, 0, 0, 0);

        const oldDateClone = new Date(oldValue.getTime());

        oldDateClone.setHours(0, 0, 0, 0);

        if (newDateClone.getTime() === oldDateClone.getTime()) return;

        if (newValueWithLimitCheck.getTime() !== newDateClone.getTime()) {
          newValue = newValueWithLimitCheck;
        }
      }

      const currentTaskDate = new Date(this.task[dateProp]);
      const [currentHours, currentMinutes] = [currentTaskDate.getHours(), currentTaskDate.getMinutes()];

      this.getClosestDate({
        date: newValue,
        dir: dateProp === 'start_date' ? 'future' : 'past',
        task: this.task,
      }).then(newDate => {
        newDate.setHours(currentHours);
        newDate.setMinutes(currentMinutes);
        this.handleChangeData(dateProp, newDate);
      });
    },
    toggleCardTip(flag) {
      const tippy = this.$refs.mainWrapper._tippy;

      if (this.tippyTimeout) {
        clearTimeout(this.tippyTimeout);
      }

      if (flag) {
        this.tippyTimeout = setTimeout(() => {
          tippy.show();
        }, 300);
      } else {
        this.tippyTimeout = setTimeout(() => {
          tippy.hide();
        }, 100);
      }
    },
    cardClickHandler(e) {
      if (e.target.closest('.inner_clickable')) return;

      if (!e.target.isConnected && e.composedPath().some(elem => elem.className.includes('inner_clickable'))) return;

      this.taskSettingsHandler();
    },
    taskSettingsHandler(section = null) {
      if (this.task.id === 'draft') return;

      this.showTaskSettings(this.task.id, this.columnId, section);
    },
    toggleContainersScrollListener(activate) {
      const tasksContainer = this.$refs.mainWrapper.closest('.column_content');
      const columnsContainer = this.$refs.mainWrapper.closest('.columns_container');

      [tasksContainer, columnsContainer].forEach((container) => {
        if (!container) return;

        const method = activate ? 'addEventListener' : 'removeEventListener';
        container[method]('scroll', this.onContainerScroll);
      });
    },
    onContainerScroll(e) {
      if (this.isAssignResourcesPopupShown) {
        this.hideResourcePopup();
      }

      if (this.openedContextMenus.length) {
        this.closeContextMenus();
      }

      if (this.openedDateEditorRef) {
        this.closeDatePicker();
      }
    },
    colorPaletteHandler(flag) {
      this.hideResourcePopup();
      const taskCardEditing = flag ? {
        columnId: this.columnId,
        taskId: this.task.id
      } : null;
      this.setTaskCardEditing(taskCardEditing);
      this.paletteOpened = flag;
    },
    handleClickStatusAndPriority(e, field) {
      return !this.isFieldDisabled(field, this.task)
        && this.toggleContextMenu({ ...this.task, columnId: this.columnId }, 'task', field, e.currentTarget);
    },
    createTask(isOutsideClick = false) {
      if (this.createTriggered) return;

      this.$emit('createTask', this.name.trim(), isOutsideClick);
      this.createTriggered = true;
    },
    showCalendar(refName, flag) {
      if (!flag && this.openedDateEditorRef && refName !== this.openedDateEditorRef) return;

      this.openedDateEditorRef = flag ? refName : null;
    },
    ...mapActions({
      setTaskCardEditing: 'board/setTaskCardEditing',
    }),
    closeDatePicker() {
      this.$refs[this.openedDateEditorRef].closeEditor();
    },
  },
};
</script>

<style module src="../less/card.less"></style>
